import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { Book, Users, BookOpen, AlertCircle, Calendar } from 'lucide-react';
import { cn } from '../../lib/utils';
import LateReturnsModal from '../LateReturnsModal';
import BorrowedBooksModal from '../BorrowedBooksModal';
import ReservedBooksModal from '../ReservedBooksModal';
import getLoansList from '../../api/GetLoansList';
import getReservations from '../../api/GetReservations';

const StatCard = ({ title, value, change, icon: Icon, color, delay, onClick }) => {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay, duration: 0.4 }}
            className={cn("bg-slate-900/50 backdrop-blur-xl border border-white/10 p-6 rounded-2xl relative overflow-hidden group transition-colors", onClick && "cursor-pointer hover:bg-slate-800/50")}
            onClick={onClick}
        >
            <div className={cn("absolute top-0 right-0 p-4 opacity-10 transition-transform group-hover:scale-110", color)}>
                <Icon size={80} />
            </div>

            <div className="relative z-10">
                <div className="flex items-center gap-3 mb-4">
                    <div className={cn("p-2 rounded-lg bg-white/5", color)}>
                        <Icon size={24} className="text-white" />
                    </div>
                    <span className="text-slate-400 font-medium">{title}</span>
                </div>

                <div className="flex items-end gap-3">
                    <h3 className="text-3xl font-bold text-white">{value}</h3>
                    {change != "" && <span className={cn("text-sm font-medium mb-1", change >= 0 ? "text-emerald-400" : "text-red-400")}>
                        {change > 0 ? '+' : ''}{change}%
                    </span>}
                </div>
            </div>
        </motion.div>
    );
};

export default function StatsCards({ onNavigate }) {
    const [isLateReturnsModalOpen, setIsLateReturnsModalOpen] = useState(false);
    const [isBorrowedBooksModalOpen, setIsBorrowedBooksModalOpen] = useState(false);
    const [isReservedBooksModalOpen, setIsReservedBooksModalOpen] = useState(false);
    const [lateReturnsCount, setLateReturnsCount] = useState(0);
    const [borrowedCount, setBorrowedCount] = useState(0);
    const [reservedCount, setReservedCount] = useState(0);
    const { items: books } = useSelector((state) => state.books);
    const { items: members } = useSelector((state) => state.members);
    const currentUser = useSelector((state) => state.user.currentUser);
    const API_BASE = useSelector((state) => state.user.apiBase);
    const token = useSelector((state) => state.user.currentUser.token);
    const code_etablissement = useSelector((state) => state.user.currentUser.code_etablissement);
    const delaiEmprunt = useSelector((state) => state.user.delaiEmprunt);

    useEffect(() => {
        const fetchStats = async () => {
            //console.log("StatsCards: Fetching stats...", { API_BASE, token, code_etablissement });
            try {
                let loans = await getLoansList(API_BASE, token, code_etablissement);

                // If student, only show personal loans
                if (currentUser.role === 'Étudiant') {
                    const myId = currentUser.id || currentUser.ID;
                    loans = loans.filter(loan => loan.user_id == myId);
                }

                //console.log("StatsCards: Loans processed:", loans.length);

                // Filter for active loans (not returned) AND NOT reserved
                const activeLoans = loans.filter(loan => {
                    const book = books.find(b => (b.item_id || b.id) == loan.item_id);
                    // Check if it's an active loan AND not a reservation
                    return loan.end_date.startsWith("0001") && book?.status !== 'Réservé';
                });

                //console.log(activeLoans);
                setBorrowedCount(activeLoans.length);

                // Calculate which loans are late
                const now = new Date();
                const lateCount = activeLoans.filter(loan => {
                    const startDate = new Date(loan.start_date);
                    const prolongationDate = new Date(loan.prolongation);
                    if (prolongationDate > startDate) {
                        const dueDate = new Date(prolongationDate.getTime() + delaiEmprunt * 24 * 60 * 60 * 1000);
                        return now > dueDate;
                    }
                    const dueDate = new Date(startDate.getTime() + delaiEmprunt * 24 * 60 * 60 * 1000);
                    return now > dueDate;
                }).length;

                setLateReturnsCount(lateCount);

                // Fetch reservations
                let reservations = await getReservations(API_BASE, token, { code_etablissement });
                if (currentUser.role === 'Étudiant') {
                    const myId = currentUser.id || currentUser.ID;
                    reservations = reservations.filter(res => (res.user_id || res.UserID) == myId);
                }
                setReservedCount(reservations.length);
            } catch (error) {
                console.error("Failed to fetch stats for cards:", error);
            }
        };

        if (API_BASE && token && code_etablissement) {
            fetchStats();
        }

        // Calculate reserved books from memory (deprecated, now fetched from API above)
        // const reserved = books.filter(book => book.status === 'Réservé');
        // setReservedCount(reserved.length);

    }, [API_BASE, token, code_etablissement, delaiEmprunt, currentUser, books]);

    const stats = [
        { id: 1, title: "Total Livres", value: books.length, change: "", icon: Book, color: "text-blue-500", onClick: () => onNavigate && onNavigate('Books') },
        { id: 2, title: "Membres Actifs", value: members.length, change: "", icon: Users, color: "text-purple-500", onClick: () => onNavigate && onNavigate('Members') },
        { id: 3, title: "Livres Empruntés", value: borrowedCount, change: "", icon: BookOpen, color: "text-emerald-500", onClick: () => setIsBorrowedBooksModalOpen(true) },
        { id: 4, title: "Livres Réservés", value: reservedCount, change: "", icon: Calendar, color: "text-amber-500", onClick: () => setIsReservedBooksModalOpen(true) },
        { id: 5, title: "En Retard", value: lateReturnsCount, change: "", icon: AlertCircle, color: "text-red-500", onClick: () => setIsLateReturnsModalOpen(true) },
    ].filter(stat => {
        // Hide "Membres Actifs" for students
        if (currentUser.role === 'Étudiant' && stat.title === "Membres Actifs") return false;
        return true;
    });

    return (
        <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                {stats.map((stat, index) => (
                    <StatCard key={stat.title} {...stat} delay={index * 0.1} />
                ))}
            </div>

            <LateReturnsModal
                isOpen={isLateReturnsModalOpen}
                onClose={() => setIsLateReturnsModalOpen(false)}
                onNavigate={onNavigate}
            />

            <BorrowedBooksModal
                isOpen={isBorrowedBooksModalOpen}
                onClose={() => setIsBorrowedBooksModalOpen(false)}
                onNavigate={onNavigate}
            />

            <ReservedBooksModal
                isOpen={isReservedBooksModalOpen}
                onClose={() => setIsReservedBooksModalOpen(false)}
                onNavigate={onNavigate}
            />
        </>
    );
}
